<template>
  <div>

    <div class="box">
      <div class="flex flex-wrap items-center">
        <filtering-toggle-button :filtering="filtering" />

        <div class="flex-grow mb-1">
          <list-search v-model="searchInput" @search="getProducts"/>
          <p class="text-xs mt-0 ml-1" v-if="query.search !== ''">
            Searching for <span class="text-red-500 font-bold">{{ query.search }}</span>
          </p>
        </div>

        <div class="flex mb-1">
          <div
            v-if="hasPermission('job.create')"
            class="btn btn-purple text-sm mr-2 h-10 flex items-center px-3 mb-1"
            @click="$router.push('/product/create/')"
          >
            <i class="fas fa-plus"></i>
            <span class="ml-2">New Product</span>
          </div>

          <button
            v-if="hasPermission('product.export')"
            type="button"
            class="btn btn-green text-sm px-3 flex items-center mb-1"
            :disabled="!exportFile.product.enabled"
            @click="exportProducts"
          >
            <i class="fas fa-spinner fa-spin mr-2" v-if="!exportFile.product.enabled"></i>
            <span>Export</span>
          </button>

          <button class="btn w-10 h-10 flex items-center justify-center focus:outline-none hover:text-green-500 mb-1" @click="getProducts">
            <i class="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>
      <div class="flex flex-wrap">
        <div
          class="w-full lg:w-2/12 pr-2 pt-4 border-r border-theme-darker"
          v-show="filtering.active"
        >
          <product-filters
            :filters.sync="filters"
            @updated="getProducts"
            @has-changes="filtering.hasChanges = $event"
          />
        </div>

        <div class="w-full overflow-x-auto" :class="{ 'lg:w-10/12': filtering.active}">
          <p class="text-lg text-center my-12" v-if="!hasProducts">No data to display</p>
          <div v-else>
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Stock</th>
                  <th>Sale Price</th>
                  <th>Supplier account</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, i) in products" :key="i">
                  <td class="font-bold">{{ product.id }}</td>
                  <td>{{ product.name }}</td>
                  <td>{{ product.code }}</td>
                  <td>{{ product.type | sentenceCase }}</td>
                  <td>{{ product.stock }}</td>
                  <td>
                    <span
                    class="font-bold"
                    v-tooltip="{ content: `
                      <strong>Purchase price:</strong> ${$formatMoney(product.purchase_price)}<br>
                      <strong>Purchase Tax:</strong> ${product.purchase_tax}%
                      <strong>Sale Tax:</strong> ${product.sale_tax}%<br>
                    ` }"
                    >{{ $formatMoney(product.sale_price) }}</span>
                  </td>
                  <td>
                    <template v-if="product.supplier_account">
                      #{{ product.supplier_account.id }} {{ product.supplier_account.name }} {{ product.supplier_account.code ? '| ' + product.supplier_account.code : '' }}
                    </template>
                    <template v-else> - </template>
                  </td>
                  <td>
                    <div class="flex">
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="$router.push('/product/details/' + product.id)"
                      ><i class="fas fa-info"></i></a>
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="$router.push('/product/edit/' + product.id)"
                        v-if="hasPermission('product.edit')"
                      ><i class="fas fa-edit"></i></a>
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="deleteProduct(product.id)"
                        v-if="hasPermission('product.delete')"
                      ><i class="fas fa-trash"></i></a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="w-full flex justify-center">
          <pagination-component
            class="mt-8"
            v-if="hasProducts && pagination.totalRows / pagination.perPage > 1"
            v-model="pagination.currentPage"
            :total-rows="pagination.totalRows"
            :rows-per-page="pagination.perPage"
          />
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import Fuse from 'fuse.js'
const ListSearch = resolve => require(['@/components/ListSearch'], resolve)
const PaginationComponent = resolve => require(['@/components/Pagination'], resolve)
const FilteringToggleButton = resolve => require(['@/components/filters/FilteringToggleButton'], resolve)
const ProductFilters = resolve => require(['@/components/filters/Filters'], resolve)

export default {
  components: {
    FilteringToggleButton,
    ListSearch,
    PaginationComponent,
    ProductFilters,
  },
  data() {
    return {
      products: [],
      searchInput: '',
      query: {
        search: '',
      },
      exportFile: {
        product: {
          enabled: true,
        }
      },
      filtering: {
        active: false,
        hasChanges: false,
      },
      filters: {
        supplier: {
          key: 'supplier',
          name: 'Supplier',
          active: true,
          modelSelect: {
            propData: {
              model: 'supplier',
              formName: 'filter-supplier',
              list: [],
              searchOnMount: true,
              isTaggable: false,
              noWrap: true,
            },
            listeners: {
              input: (value) => {
                this.filters.supplier.value = value
                this.filters.supplier.children.supplier_account.value = null
              },
              'update:list': (list) => this.filters.supplier.modelSelect.propData.list = list,
              select: (supplier) => {
                // get supplier accounts
                axios.get('supplier-accounts', {params:{
                  filters: {
                    supplier_id: supplier.id
                  }
                }}).then(response => {
                  this.filters.supplier.children.supplier_account.value = null
                  this.filters.supplier.children.supplier_account.modelSelect.propData.list = response.data.data.map(e => {
                    return {id: e.id, display_name: '#' + e.id + ' ' + e.name + ' ' + e.code}
                  })
                })
              }
            }
          },
          value: null,
          children: {
            supplier_account: {
              key: 'supplier_account',
              name: 'Supplier account',
              active: true,
              showWhenParentValue: true,
              modelSelect: {
                propData: {
                  model: 'supplier-account',
                  formName: 'filter-supplier-account',
                  list: [],
                  searchOnMount: true,
                  isTaggable: false,
                  noWrap: true,
                },
                listeners: {
                  input: (value) => {
                    this.filters.supplier.children.supplier_account.value = value
                  },
                  search: (query) => {
                    let fuse = new Fuse(this.filters.supplier.children.supplier_account.modelSelect.propData.list, {
                      keys: ['display_name']
                    })
                    fuse.search(query)
                  }
                }
              },
              value: null,
            }
          }
        },
        createdAt: {
          key: 'created_at',
          name: 'Created at date',
          active: true,
          flatpickr: {
            mode: 'range',
            static: false,
          },
          value: null,
        },
      },
      pagination: {
        currentPage: 1,
        totalRows: 0,
        perPage: 0,
      }
    }
  },
  watch: {
    'pagination.currentPage'() {
      this.getProducts()
    },
    searchInput(i) {
      if (i === '' && this.query.search !== '') {
        this.getProducts()
      }
    }
  },
  computed: {
    hasProducts() {
      return Object.entries(this.products).length > 0
    },
    ...mapGetters({
      hasPermission: 'user/hasPermission',
      config: 'config/config',
    })
  },
  mounted() {
    this.getProducts()
  },
  methods: {
    exportProducts() {
      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }

      if (this.filters.supplier.value) {
        filters.supplier_id = this.filters.supplier.value.id
      }

      this.exportFile.product.enabled = false
      axios.get('products/export', {
        params: {
          search: this.searchInput,
          filters,
        },
        responseType: 'arraybuffer'
      })
        .then((response) => {
          this.$downloadFile(response)
          this.exportFile.product.enabled = true
        })
    },
    deleteProduct(productID) {
      this.$swal.fire({
        title: 'Delete product',
        text: 'Are you sure?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete'
      })
        .then((result) => {
          if (result.value) {
            // send delete
            axios.delete('products/' + productID)
              .then(() => {
                this.$swal.fire('Delete successful', '', 'success')
                this.getProducts()
              })
          }
        })
    },
    getProducts() {
      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }

      if (this.filters.supplier.value) {
        filters.supplier_id = this.filters.supplier.value.id
        if (this.filters.supplier.children.supplier_account.value) {
          filters.supplier_account_id = this.filters.supplier.children.supplier_account.value.id
        }
      }

      axios.get('products', {params:{
        page: this.pagination.currentPage,
        search: this.searchInput,
        filters,
      }})
        .then(response => {
          if (response.data) {
            const res = response.data
            this.pagination.perPage = res.per_page
            this.pagination.totalRows = res.total
            this.products = Object.assign({}, res.data)

            this.query.search = this.searchInput
          }
        })
    }
  }
}
</script>
